import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import {
    GENERATE_WITH_PROMPT,
    GENERATE_IMAGE_FROM_IMAGE,
    POLL_GENERATED_IMAGES,
    GENERATED_IMAGES,
    DELETE_IMAGES,
} from '@/graphql/ads/generated-image-queries'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

const REPLACEMENT_FAILED = 'The refined image could not be generated, so the original was kept.'
const SOURCE_NOT_REMOVED = 'The refined image is in, but the original could not be removed.'

const notifyError = (message) => showNotification(message, 'error', 5000, null, null, false, true)

const swapId = (ids, from, to) => ids.map((id) => (id === from ? to : id))

export const useImageGeneration = (mode = 'textToImage') => {
    const { campaignId } = useWizard()
    const [selectedFormats, setSelectedFormats] = useState({
        square: true,
        vertical: true,
        horizontal: true,
    })
    const [prompt, setPrompt] = useState('')
    const [isGenerating, setIsGenerating] = useState(false)
    const [currentlyGeneratingImageIds, setCurrentlyGeneratingImageIds] = useState([])
    const [generatedImages, setGeneratedImages] = useState([])
    const [selectedImages, setSelectedImages] = useState([])
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
    const [idsToDelete, setIdsToDelete] = useState([])
    const [generationProgress, setGenerationProgress] = useState({
        current: 0,
        total: 0,
        percentage: 0,
        startTime: null,
    })
    // The source each refined image still generating replaces, by the refined image's id
    const replacementsRef = useRef({})
    // The latest settleReplacements, so the polling effect need not depend on it
    const settleRef = useRef(() => [])

    const [generateImage, { loading }] = useMutation(GENERATE_WITH_PROMPT)
    const [generateImageFromImage, { loading: loadingImageToImage }] = useMutation(GENERATE_IMAGE_FROM_IMAGE)
    const [deleteImage] = useMutation(DELETE_IMAGES)

    const {
        data: pollingData,
        startPolling,
        stopPolling,
    } = useQuery(POLL_GENERATED_IMAGES, {
        variables: { ids: currentlyGeneratingImageIds },
        skip: currentlyGeneratingImageIds.length === 0,
        pollInterval: 2000,
        fetchPolicy: 'network-only',
    })

    const { refetch: refetchImages } = useQuery(GENERATED_IMAGES, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const images = data.generatedImages
            setGeneratedImages(images)

            const processingImages = images.filter((x) => x.status === 'processing')
            if (processingImages.length > 0) {
                setIsGenerating(true)
                startPolling(2000)
                setCurrentlyGeneratingImageIds(processingImages.map((x) => x.id))

                // Initialize progress state for simulation on page reload
                setGenerationProgress({
                    current: 0,
                    total: processingImages.length,
                    percentage: 0,
                    startTime: Date.now(),
                })
            }
        },
    })

    useEffect(() => {
        const generatedImages = pollingData?.generatedImagesByIds
        const allProcessed = generatedImages && !generatedImages.some((x) => x.status === 'processing')

        if (generatedImages) {
            setGeneratedImages((prev) =>
                prev.map((img) => {
                    const updated = generatedImages.find((u) => u.id === img.id)
                    return updated ? { ...img, ...updated } : img
                }),
            )
            settleRef.current(generatedImages)

            // Update progress based on completed images
            const completedCount = generatedImages.filter((x) => x.status !== 'processing').length
            setGenerationProgress((prev) => ({
                ...prev,
                current: completedCount,
                total: prev.total === 0 ? generatedImages.length : prev.total,
            }))
        }

        if (allProcessed) {
            setCurrentlyGeneratingImageIds([])
            stopPolling()
            setIsGenerating(false)

            // Set to 100% immediately when done
            setGenerationProgress((prev) => ({
                ...prev,
                percentage: 100,
            }))

            // Reset after showing completion
            setTimeout(() => {
                setGenerationProgress({
                    current: 0,
                    total: 0,
                    percentage: 0,
                    startTime: null,
                })
            }, 1000)
        }
    }, [pollingData, stopPolling])

    // Simple linear progress simulation
    useEffect(() => {
        if (!isGenerating || generationProgress.total === 0) return

        const interval = setInterval(() => {
            setGenerationProgress((prev) => {
                if (!prev.startTime) return prev

                const elapsed = Math.floor((Date.now() - prev.startTime) / 1000) // seconds elapsed
                const timePerImage = 20 // 20 seconds per image estimate
                const totalEstimatedTime = prev.total * timePerImage

                // Calculate actual progress
                const actualPercentage = (prev.current / prev.total) * 100

                // Progressive slowdown simulation - never stops, just slows down
                let simulatedPercentage
                if (elapsed <= totalEstimatedTime) {
                    // Normal speed for first 80% of estimated time
                    const normalProgress = (elapsed / totalEstimatedTime) * 80
                    simulatedPercentage = normalProgress
                } else {
                    // Gradual slowdown after estimated time
                    const overtimeSeconds = elapsed - totalEstimatedTime
                    // Start at 80%, add diminishing progress
                    // Each 10 seconds adds less progress: 5%, 2.5%, 1.25%, etc.
                    let additionalProgress = 0
                    let increment = 5
                    let timeMarker = 0

                    while (timeMarker < overtimeSeconds) {
                        const timeInThisSegment = Math.min(10, overtimeSeconds - timeMarker)
                        additionalProgress += (timeInThisSegment / 10) * increment
                        increment = increment / 2 // Halve the increment each 10 seconds
                        timeMarker += 10

                        // Safety brake - never go above 99%
                        if (additionalProgress > 19) {
                            additionalProgress = 19
                            break
                        }
                    }

                    simulatedPercentage = 80 + additionalProgress
                }

                // Use whichever is higher
                const percentage = Math.max(simulatedPercentage, actualPercentage)

                return {
                    ...prev,
                    percentage: Math.round(percentage),
                }
            })
        }, 1000) // Update every second

        return () => clearInterval(interval)
    }, [isGenerating, generationProgress.total])

    // Reloads the library
    const refetch = async () => {
        const { data } = await refetchImages()
        if (data?.generatedImages) setGeneratedImages(data.generatedImages)
    }

    // Removes the source a refined image replaced; the list already shows the refined one in its place
    const removeReplacedSource = (source) =>
        deleteImage({ variables: { ids: [source.id] } }).catch(() => notifyError(SOURCE_NOT_REMOVED))

    // Puts a completed refined image in its source's place (and selection) and removes the source
    const replaceAsset = (source, asset) => {
        setGeneratedImages((prev) =>
            prev.some((img) => img.id === source.id)
                ? prev.map((img) => (img.id === source.id ? asset : img))
                : [asset, ...prev],
        )
        setSelectedImages((prev) => swapId(prev, source.id, asset.id))
        return removeReplacedSource(source)
    }

    // Finishes the replacements among polled records: a completed one drops its source, a failed one gives
    // the source's place back
    const settleReplacements = (records) => {
        records.forEach((record) => {
            const source = replacementsRef.current[record.id]
            if (!source || record.status === 'processing') return
            delete replacementsRef.current[record.id]
            if (record.status === 'completed') {
                removeReplacedSource(source)
            } else {
                setGeneratedImages((prev) => prev.map((img) => (img.id === record.id ? source : img)))
                setSelectedImages((prev) => swapId(prev, record.id, source.id))
                notifyError(REPLACEMENT_FAILED)
            }
        })
    }
    settleRef.current = settleReplacements

    // Shows a record another flow started (a refine turn closed mid-generation) and polls it like our own; with
    // `replaces`, the record takes that source's place and the source goes once the record completes
    const trackGenerating = (record, replaces = null) => {
        if (replaces) {
            replacementsRef.current[record.id] = replaces
            setSelectedImages((prev) => swapId(prev, replaces.id, record.id))
        }
        setGeneratedImages((prev) => {
            if (prev.some((img) => img.id === record.id)) return prev
            if (replaces && prev.some((img) => img.id === replaces.id)) {
                return prev.map((img) => (img.id === replaces.id ? record : img))
            }
            return [record, ...prev]
        })
        setCurrentlyGeneratingImageIds((prev) => (prev.includes(record.id) ? prev : [record.id, ...prev]))
        setIsGenerating(true)
        setGenerationProgress((prev) =>
            prev.total > 0
                ? { ...prev, total: prev.total + 1 }
                : { current: 0, total: 1, percentage: 0, startTime: Date.now() },
        )
        setTimeout(() => startPolling(2000), 0)
    }

    const handleFormatSelection = (ratio) => {
        setSelectedFormats((prev) => ({
            ...prev,
            [ratio]: !prev[ratio],
        }))
    }

    const handleGenerate = async () => {
        const formats = Object.keys(selectedFormats).filter((key) => selectedFormats[key])
        const totalImages = formats.length // 1 image per format

        // Initialize simple progress
        setGenerationProgress({
            current: 0,
            total: totalImages,
            percentage: 0,
            startTime: Date.now(),
        })

        let data
        if (mode === 'imageToImage') {
            if (selectedImages.length === 0) return

            // Extract ID, source, and URL from first selected item (supports both string ID and object format)
            const firstSelected = selectedImages[0]
            let referenceImageId = typeof firstSelected === 'string' ? firstSelected : firstSelected.id
            const referenceImageSource = typeof firstSelected === 'string' ? null : firstSelected.source

            // For Meta images, use the URL instead of the ID
            if (referenceImageSource === 'meta' && firstSelected.url) {
                referenceImageId = firstSelected.url
            }

            const result = await generateImageFromImage({
                variables: {
                    input: {
                        prompt,
                        formats,
                        referenceImageId,
                        ...(referenceImageSource && { referenceImageSource }),
                        ...(campaignId && { campaignId }),
                    },
                },
            })
            data = result.data
        } else {
            const result = await generateImage({
                variables: {
                    input: { prompt, formats, ...(campaignId && { campaignId }) },
                },
            })
            data = result.data
        }

        const generatedImageResults = data?.generateImage || data?.generateImageFromImage

        if (generatedImageResults?.length) {
            const ids = generatedImageResults.map((x) => x.id)
            setGeneratedImages((prev) => [...generatedImageResults, ...prev])

            setCurrentlyGeneratingImageIds((prev) => [...ids, ...prev])
            setIsGenerating(true)
            setTimeout(() => startPolling(2000), 0)
        }
    }

    const handleStopGenerate = () => {
        handleDelete(currentlyGeneratingImageIds)
    }

    const handleDelete = (ids) => {
        setIdsToDelete(ids)
        setShowDeleteConfirmationModal(true)
    }

    const handleConfirmDelete = () => {
        deleteImage({ variables: { ids: idsToDelete } })
        setGeneratedImages((prev) => prev.filter((image) => !idsToDelete.includes(image.id)))
        setCurrentlyGeneratingImageIds((prev) => prev.filter((id) => !idsToDelete.includes(id)))
        setShowDeleteConfirmationModal(false)
        setIsGenerating(false)
        setIdsToDelete([])
    }

    const handleSelect = (allowMultiple, id, source = null, url = null) => {
        if (!allowMultiple && selectedImages.length > 0) {
            // If source is provided, store as object, otherwise just ID for backward compatibility
            setSelectedImages([source ? { id, source, url } : id])
        } else {
            setSelectedImages((prev) => {
                // Check if item is already selected
                const isSelected = prev.some((item) => (typeof item === 'string' ? item === id : item.id === id))

                if (isSelected) {
                    // Remove item
                    return prev.filter((item) => (typeof item === 'string' ? item !== id : item.id !== id))
                } else {
                    // Add item
                    const newItem = source ? { id, source, url } : id
                    return [...prev, newItem]
                }
            })
        }
    }

    return {
        // State
        selectedFormats,
        prompt,
        isGenerating,
        generatedImages,
        selectedImages,
        showDeleteConfirmationModal,
        loading: loading || loadingImageToImage,
        generationProgress,

        // Handlers
        refetch,
        replaceAsset,
        trackGenerating,
        setPrompt,
        handleFormatSelection,
        handleGenerate,
        handleStopGenerate,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
        setSelectedImages: (value) => {
            // Support both formats:
            // 1. Array of IDs (strings)
            // 2. Array of objects with {id, source}
            // 3. Function updater
            if (typeof value === 'function') {
                setSelectedImages(value)
            } else if (Array.isArray(value)) {
                // Normalize to internal format if needed
                const normalized = value.map((item) => {
                    if (typeof item === 'string') {
                        // Keep as string for backward compatibility
                        return item
                    } else if (item && typeof item === 'object' && 'id' in item) {
                        // Already in object format, ensure it has url if available
                        return item
                    }
                    return item
                })
                setSelectedImages(normalized)
            } else {
                setSelectedImages(value)
            }
        },
    }
}
